import type { Segment } from './types';

export interface RenderConfig {
  width: number;
  height: number;
  text: string;
  duration: number; // seconds
  textColor: string;
  backgroundColor: string;
  fontName: string;
  fontSize: number;
  textAlign: 'left' | 'center' | 'right';
  lineHeight: number;
  textShadow: number;
  blur: number;
  vignette: number;
  renderMode: 'classic' | 'sync';
  segments?: Segment[];
  backgroundImage?: ImageBitmap | HTMLImageElement | null;
  backgroundVideo?: HTMLVideoElement | null;
}

export class ScrollRenderer {
  private textBitmap: OffscreenCanvas | null = null;
  private textTotalHeight = 0;
  public width: number;
  public height: number;
  private frameCanvas: OffscreenCanvas;
  private frameCtx: OffscreenCanvasRenderingContext2D;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.frameCanvas = new OffscreenCanvas(width, height);
    this.frameCtx = this.frameCanvas.getContext('2d')!;
  }

  private lastText = "";
  private lastFont = "";
  private lastSize = 0;
  private lastWidth = 0;
  private lastAlign = "";
  private lastLineHeight = 0;
  private lastShadow = -1;

  prepareText(config: RenderConfig, force = false) {
    if (config.renderMode === 'sync') return; // Handled frame-by-frame

    const { text, width, fontName, fontSize, textColor, textAlign, lineHeight, textShadow } = config;
    
    const needsRebuild = force || 
      text !== this.lastText || 
      fontName !== this.lastFont || 
      fontSize !== this.lastSize || 
      width !== this.lastWidth ||
      textAlign !== this.lastAlign ||
      lineHeight !== this.lastLineHeight ||
      textShadow !== this.lastShadow;

    if (!needsRebuild && this.textBitmap) return;

    this.lastText = text;
    this.lastFont = fontName;
    this.lastSize = fontSize;
    this.lastWidth = width;
    this.lastAlign = textAlign;
    this.lastLineHeight = lineHeight;
    this.lastShadow = textShadow;

    const effectiveLineHeight = Math.ceil(fontSize * lineHeight);
    const paddingX = Math.round(width * 0.1);
    const maxWidth = width - paddingX * 2;

    const tempCanvas = new OffscreenCanvas(width, 100);
    const tempCtx = tempCanvas.getContext('2d')!;
    tempCtx.font = `bold ${fontSize}px "${fontName}", sans-serif`;

    const paragraphs = text.split('\n');
    const lines: string[] = [];

    for (const para of paragraphs) {
      if (para.trim() === '') {
        lines.push('');
        continue;
      }
      lines.push(...this.wrapText(tempCtx, para, maxWidth));
    }

    const totalHeight = lines.length * effectiveLineHeight + 40;
    const canvasHeight = Math.min(totalHeight, 16384);

    this.textBitmap = new OffscreenCanvas(width, canvasHeight);
    const ctx = this.textBitmap.getContext('2d')!;

    if (textShadow > 0) {
      ctx.shadowColor = 'rgba(0,0,0,0.8)';
      ctx.shadowBlur = textShadow;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    }

    ctx.font = `bold ${fontSize}px "${fontName}", sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textBaseline = 'top';
    ctx.textAlign = textAlign;

    let y = 20;
    const drawX = textAlign === 'center' ? width / 2 : textAlign === 'right' ? width - paddingX : paddingX;
    
    for (const line of lines) {
      if (y + effectiveLineHeight > canvasHeight) break;
      ctx.fillText(line, drawX, y);
      y += effectiveLineHeight;
    }

    this.textTotalHeight = y;
  }

  private wrapText(ctx: OffscreenCanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);
    return lines;
  }

  renderFrame(config: RenderConfig, currentTime: number): OffscreenCanvas {
    const { width, height, backgroundColor, duration, blur, vignette } = config;
    const ctx = this.frameCtx;

    ctx.filter = 'none';
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);

    if (blur > 0) {
      const blurPx = (blur / 100) * 64;
      ctx.filter = `blur(${blurPx}px)`;
    }

    if (config.backgroundVideo) {
      try { ctx.drawImage(config.backgroundVideo, 0, 0, width, height); } catch { /* not ready */ }
    } else if (config.backgroundImage) {
      try { ctx.drawImage(config.backgroundImage, 0, 0, width, height); } catch { /* not loaded */ }
    }

    ctx.filter = 'none';

    if (Math.abs(vignette) > 0) {
      const gradient = ctx.createRadialGradient(
        width / 2, height / 2, 0,
        width / 2, height / 2, Math.sqrt((width / 2) ** 2 + (height / 2) ** 2)
      );
      
      const opacity = (Math.abs(vignette) / 100) * 0.8;
      const color = vignette > 0 ? '0,0,0' : '255,255,255';
      
      gradient.addColorStop(0, 'transparent');
      gradient.addColorStop(0.5, 'transparent');
      gradient.addColorStop(1, `rgba(${color},${opacity})`);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }

    if (config.renderMode === 'classic') {
      if (this.textBitmap && this.textTotalHeight > 0) {
        const progress = Math.min(currentTime / duration, 1);
        const startY = height / 2;
        const endY = height / 2 - this.textTotalHeight;
        const scrollY = startY + (endY - startY) * progress;
        ctx.drawImage(this.textBitmap, 0, Math.round(scrollY));
      }
    } else if (config.renderMode === 'sync' && config.segments) {
      this.renderSyncText(config, currentTime);
    }

    return this.frameCanvas;
  }

  private renderSyncText(config: RenderConfig, currentTime: number) {
    const { width, height, segments, fontName, fontSize, textColor, textAlign, lineHeight, textShadow } = config;
    if (!segments) return;

    const activeSegment = segments.find(s => currentTime >= s.startTime && currentTime <= s.endTime);
    if (!activeSegment) return;

    const ctx = this.frameCtx;
    
    if (textShadow > 0) {
      ctx.shadowColor = 'rgba(0,0,0,0.8)';
      ctx.shadowBlur = textShadow;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    }

    ctx.font = `bold ${fontSize}px "${fontName}", sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textAlign = textAlign;
    ctx.textBaseline = 'middle';

    let opacity = 1;
    if (activeSegment.transition === 'fade') {
      const fadeDuration = 0.2;
      const timeInSegment = currentTime - activeSegment.startTime;
      const timeToEnd = activeSegment.endTime - currentTime;
      
      if (timeInSegment < fadeDuration) {
        opacity = timeInSegment / fadeDuration;
      } else if (timeToEnd < fadeDuration) {
        opacity = timeToEnd / fadeDuration;
      }
    }

    ctx.globalAlpha = opacity;
    
    const paddingX = Math.round(width * 0.1);
    const lines = this.wrapText(ctx, activeSegment.text, width - paddingX * 2);
    const effectiveLineHeight = fontSize * lineHeight;
    const totalHeight = lines.length * effectiveLineHeight;
    
    let y = (height - totalHeight) / 2 + effectiveLineHeight / 2;
    const drawX = textAlign === 'center' ? width / 2 : textAlign === 'right' ? width - paddingX : paddingX;

    for (const line of lines) {
      ctx.fillText(line, drawX, y);
      y += effectiveLineHeight;
    }

    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0; // Reset shadow
  }

  destroy() {
    this.textBitmap = null;
  }
}
